import { Tooltip } from "antd"
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react"
import type { ValueType } from "@csbi/metric-catalog"
import { formatValue, ink, seriesPalette, type ThemeMode, type ValueFormat } from "@csbi/viz-core"

export type KpiDelta = {
  ratio: number
  comparedTo: string
  good: boolean | null
}

export type KpiCardProps = {
  label: string
  value: number | string | null
  valueType?: ValueType
  format?: ValueFormat
  mode: ThemeMode
  hero?: boolean
  delta?: KpiDelta
  /** 12-point sparkline. Pass null for a missing period, **never 0** */
  trend?: readonly (number | null)[]
  caveat?: string
  suffix?: string
}

export function KpiCard({
  label,
  value,
  valueType = "count",
  format = "auto",
  mode,
  hero = false,
  delta,
  trend,
  caveat,
  suffix,
}: KpiCardProps) {
  const c = ink(mode)
  const text = formatValue(value, { valueType, format, context: "label" })

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
      <div style={{ fontSize: 12, color: c.secondary, display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        {caveat && (
          <Tooltip title={caveat}>
            <span style={{ fontSize: 11, color: "var(--csbi-status-warning-ink)", cursor: "help" }}>approx.</span>
          </Tooltip>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 6, minWidth: 0 }}>
        <span
          style={{
            fontSize: hero ? 48 : 32,
            fontWeight: 600,
            lineHeight: 1.1,
            color: c.primary,
            // Proportional figures: deliberately no tabular-nums
            fontVariantNumeric: "proportional-nums",
          }}
        >
          {text}
        </span>
        {suffix && <span style={{ fontSize: 13, color: c.muted }}>{suffix}</span>}
      </div>

      {delta && <DeltaLine delta={delta} mode={mode} />}
      {trend && trend.length > 1 && <Sparkline points={trend} mode={mode} />}
    </div>
  )
}

function DeltaLine({ delta, mode }: { delta: KpiDelta; mode: ThemeMode }) {
  const c = ink(mode)
  const flat = delta.ratio === 0 || delta.good === null
  const Icon = flat ? Minus : delta.ratio > 0 ? ArrowUpRight : ArrowDownRight
  const color = flat ? c.muted : delta.good ? "var(--csbi-status-good-ink)" : "var(--csbi-status-critical-ink)"
  const pct = `${delta.ratio > 0 ? "+" : ""}${(delta.ratio * 100).toFixed(1)}%`

  return (
    <Tooltip title={`Compared with ${delta.comparedTo}`}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12, color }}>
        <Icon size={13} aria-hidden="true" />
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{flat ? "no change" : pct}</span>
        <span style={{ color: c.muted }}>vs {delta.comparedTo}</span>
      </span>
    </Tooltip>
  )
}

function Sparkline({ points, mode }: { points: readonly (number | null)[]; mode: ThemeMode }) {
  const c = ink(mode)
  const nums = points.filter((v): v is number => v != null)
  if (nums.length < 2) return null

  const min = Math.min(...nums)
  const max = Math.max(...nums)
  const span = max - min || 1
  const w = 96
  const h = 22
  const step = w / (points.length - 1)

  // Missing periods break the line: a gap is more honest than "draw a straight segment"
  const segments: string[] = []
  let current: string[] = []
  points.forEach((v, i) => {
    if (v == null) {
      if (current.length > 1) segments.push(current.join(" "))
      current = []
      return
    }
    current.push(`${(i * step).toFixed(1)},${(h - ((v - min) / span) * (h - 3) - 1.5).toFixed(1)}`)
  })
  if (current.length > 1) segments.push(current.join(" "))

  const lastIndex = points.findLastIndex((v) => v != null)
  const last = points[lastIndex]

  return (
    <svg width={w} height={h} aria-hidden="true" style={{ display: "block", marginTop: 2 }}>
      {segments.map((pts, i) => (
        <polyline key={i} points={pts} fill="none" stroke={c.gridline} strokeWidth="1.5" strokeLinecap="round" />
      ))}
      {last != null && (
        <circle
          cx={(lastIndex * step).toFixed(1)}
          cy={(h - ((last - min) / span) * (h - 3) - 1.5).toFixed(1)}
          r="2.5"
          fill={seriesPalette(mode)[0]}
        />
      )}
    </svg>
  )
}
